import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, Trash2, Plus, Minus, ArrowLeft, Image as ImageIcon } from 'lucide-react';
import { Button } from '@caruso/ui';
import { useCart } from '@/contexts/cart-context';
import { StoreLayout } from '@/components/layouts/store-layout';

// Componente para exibir imagem do produto com fallback
function ProductImage({ src, alt, size = 'md' }: { src?: string; alt: string; size?: 'sm' | 'md' | 'lg' }) {
  const [imageError, setImageError] = useState(false);
  const hasImage = src && !imageError;
  
  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32',
  };

  return (
    <div className={`${sizeClasses[size]} bg-brand-smoke/20 border border-brand-gold/20 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0`}>
      {hasImage ? (
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      ) : (
        <ImageIcon className={`${size === 'sm' ? 'w-8 h-8' : size === 'md' ? 'w-10 h-10' : 'w-12 h-12'} text-brand-gold/40`} />
      )}
    </div>
  );
}

export function StoreCartPage() {
  const navigate = useNavigate();
  const { items, removeItem, updateQuantity, totalItems, totalPrice, clearCart } = useCart();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  const handleUpdateQuantity = (id: string, newQuantity: number) => {
    if (newQuantity < 1) {
      removeItem(id);
      return;
    }
    updateQuantity(id, newQuantity);
  };

  if (items.length === 0) {
    return (
      <StoreLayout>
        <div className="text-center py-16">
          <ShoppingCart className="w-24 h-24 text-brand-gold/40 mx-auto mb-6" />
          <h2 className="text-2xl font-serif text-brand-gold mb-4">Seu carrinho está vazio</h2>
          <p className="text-brand-ivory/60 mb-8">Adicione produtos ao carrinho para continuar</p>
          <Button onClick={() => navigate('/loja')} variant="outline">
            Continuar Comprando
          </Button>
        </div>
      </StoreLayout>
    );
  }

  return (
    <StoreLayout>
      <div className="space-y-6">
        {/* Back button */}
        <Button variant="ghost" onClick={() => navigate('/loja')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Continuar comprando
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-serif text-brand-gold">Carrinho de Compras</h2>
              <Button variant="ghost" size="sm" onClick={clearCart} className="text-red-400 hover:text-red-300">
                <Trash2 className="w-4 h-4 mr-2" />
                Limpar carrinho
              </Button>
            </div>

            {items.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -100 }}
                className="bg-brand-ink/50 border border-brand-gold/20 rounded-lg p-4"
              >
                <div className="flex gap-4">
                  {/* Image */}
                  <ProductImage src={item.image} alt={item.name} size="md" />

                  {/* Info */}
                  <div className="flex-1">
                    <h3 className="text-brand-gold font-semibold mb-1">{item.name}</h3>
                    <p className="text-brand-ivory/60 text-sm mb-3">{formatPrice(item.price)} cada</p>

                    <div className="flex items-center justify-between">
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="text-brand-ivory font-semibold w-8 text-center">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                          disabled={item.stock !== undefined && item.stock !== null && item.quantity >= item.stock}
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>

                      {/* Subtotal and Remove */}
                      <div className="flex items-center gap-4">
                        <p className="text-brand-gold font-bold text-lg">
                          {formatPrice(item.price * item.quantity)}
                        </p>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItem(item.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-brand-ink/50 border border-brand-gold/20 rounded-lg p-6 sticky top-24">
              <h3 className="text-xl font-serif text-brand-gold mb-6">Resumo do Pedido</h3>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-brand-ivory/70">
                  <span>Subtotal ({totalItems} {totalItems === 1 ? 'item' : 'itens'})</span>
                  <span>{formatPrice(totalPrice)}</span>
                </div>
                <div className="flex justify-between text-brand-ivory/70">
                  <span>Frete</span>
                  <span className="text-sm">Calculado no checkout</span>
                </div>
                <div className="border-t border-brand-gold/20 pt-3 mt-3">
                  <div className="flex justify-between text-brand-gold text-xl font-bold">
                    <span>Total</span>
                    <span>{formatPrice(totalPrice)}</span>
                  </div>
                </div>
              </div>

              <Button
                className="w-full h-12 text-lg"
                onClick={() => navigate('/loja/checkout')}
              >
                Finalizar Compra
              </Button>

              <p className="text-brand-ivory/50 text-xs text-center mt-4">
                Frete e impostos calculados no checkout
              </p>
            </div>
          </div>
        </div>
      </div>
    </StoreLayout>
  );
}
